
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, Mail, Truck, Recycle, Factory } from "lucide-react";
import { DepartmentSEOContent } from "@/data/geographic/departmentContent";

interface GeoLocalizedContentProps {
  content: DepartmentSEOContent;
  showCTA?: boolean;
}

const GeoLocalizedContent: React.FC<GeoLocalizedContentProps> = ({ 
  content, 
  showCTA = true 
}) => {
  return (
    <div className="space-y-12">
      {/* Hero Section géolocalisée */}
      <section className="bg-gradient-to-r from-fabre-green to-fabre-darkgreen text-white py-16 rounded-lg">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">{content.heroTitle}</h1>
          <p className="text-xl mb-6">{content.heroSubtitle}</p>
          <div className="flex items-center justify-center gap-2 text-fabre-lightgreen">
            <MapPin className="h-5 w-5" />
            <span>{content.name} ({content.code}) - {content.region}</span>
          </div>
        </div>
      </section>

      {/* Services par zone géographique */}
      <section className="bg-white rounded-lg shadow-md p-8">
        <h2 className="text-2xl font-bold text-fabre-gray mb-6">
          Nos services dans le {content.name}
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          {content.services.map((service, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                {index === 0 ? <Truck className="h-6 w-6 text-fabre-green" /> : 
                 index === 1 ? <Recycle className="h-6 w-6 text-fabre-green" /> :
                 <Factory className="h-6 w-6 text-fabre-green" />}
                <h3 className="text-xl font-semibold text-fabre-darkgreen">{service.title}</h3>
              </div>
              <p className="text-gray-600 mb-4">{service.description}</p>
              <div className="flex flex-wrap gap-2">
                {service.keywords.map((keyword, idx) => (
                  <span key={idx} className="bg-fabre-lightgreen text-fabre-darkgreen px-3 py-1 rounded-full text-sm">
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Zones d'intervention */}
      <section className="bg-gray-50 rounded-lg p-8">
        <h2 className="text-2xl font-bold text-fabre-gray mb-6">
          Nos zones d'intervention dans le {content.name}
        </h2>
        <p className="text-gray-600 mb-6">
          Nous intervenons dans l'ensemble du département du {content.name} ({content.code}), 
          notamment dans les principales villes et communes suivantes :
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {content.mainCities.map((city, index) => (
            <div key={index} className="bg-white p-3 rounded-lg shadow-sm text-center">
              <MapPin className="h-5 w-5 text-fabre-green mx-auto mb-2" />
              <span className="text-sm font-medium text-gray-700">{city}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Informations locales */}
      <section className="bg-white rounded-lg shadow-md p-8">
        <h2 className="text-2xl font-bold text-fabre-gray mb-6">
          Le {content.name} et l'environnement
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-fabre-darkgreen mb-3">Population</h3>
            <p className="text-gray-600">{content.localInfo.population}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-fabre-darkgreen mb-3">Activités économiques</h3>
            <ul className="text-gray-600 space-y-1">
              {content.localInfo.economicActivity.map((activity, index) => (
                <li key={index}>• {activity}</li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-fabre-darkgreen mb-3">Enjeux environnementaux</h3>
            <ul className="text-gray-600 space-y-1">
              {content.localInfo.environmentalChallenges.map((challenge, index) => (
                <li key={index}>• {challenge}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Services spécialisés */}
      <section className="bg-fabre-lightgreen rounded-lg p-8">
        <h2 className="text-2xl font-bold text-fabre-darkgreen mb-6">
          Services spécialisés pour le {content.name}
        </h2>
        <p className="text-gray-700 mb-4">
          Grâce à notre connaissance du territoire {content.name.toLowerCase()}, nous proposons des services 
          adaptés aux spécificités locales :
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          {content.specificServices.map((service, index) => (
            <div key={index} className="bg-white p-4 rounded-lg shadow-sm">
              <span className="text-fabre-darkgreen font-medium">{service}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA géolocalisé */}
      {showCTA && (
        <section className="bg-gradient-to-r from-fabre-darkgreen to-fabre-green text-white rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">
            Besoin de nos services dans le {content.name} ?
          </h2>
          <p className="text-lg mb-6">
            Notre équipe locale connaît parfaitement les enjeux du territoire {content.name.toLowerCase()}. 
            Contactez-nous pour une solution personnalisée.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild size="lg" className="bg-white text-fabre-green hover:bg-gray-100">
              <Link to="/contact" className="flex items-center">
                <Mail className="mr-2 h-5 w-5" />
                Devis gratuit
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-fabre-green">
              <a href="tel:+33565787900" className="flex items-center">
                <Phone className="mr-2 h-5 w-5" />
                05 65 78 79 00
              </a>
            </Button>
          </div>
        </section>
      )}
    </div>
  );
};

export default GeoLocalizedContent;
